import { useEffect, useState } from "react";
import { Head } from "vite-react-ssg";
import { FaWhatsapp } from "react-icons/fa";
import mascote from "@/assets/camping-mascot.webp";
import { SeoHead } from "@/components/seo/SeoHead";
import { trackLeadAndGo, generateEventId } from "@/lib/facebookPixel";
import { trackCtaClick, buildOutboundUrl } from "@/lib/analytics";
import { GRUPO_CAMPING } from "@/config/grupos";
import { aplicarCeu } from "@/lib/ceu";

/**
 * /camping — landing do grupo de Camping (2026-10-07, pedido do operador).
 *
 * Diferente da home de propósito:
 *   - SÓ WhatsApp. Sem Telegram, sem vitrine, sem sticky bar.
 *   - Paleta pastel (sálvia, aveia, pêssego) em vez do vermelho da marca. O
 *     vermelho é FROZEN para a home; aqui é outra landing, outro público.
 *   - Os overlays globais da home (exit intent, push, PWA, live banner) ficam
 *     DESLIGADOS nesta rota (ver ROTAS_ISOLADAS em App.tsx). O exit intent
 *     mandaria o visitante pro grupo PRINCIPAL e ainda mostraria Telegram.
 *
 * O clique dispara o MESMO Lead do pixel "Macaco Ofertas" (662711039631989),
 * Pixel + CAPI com o mesmo event_id, igual à home. O conjunto de anúncios de
 * camping otimiza por esse evento. O que muda é só o content_name
 * ("WhatsApp Group - Camping") e o destination ("whatsapp_camping_group"), para
 * dar pra separar os dois públicos no Gerenciador de Eventos.
 *
 * Sem contador de membros: o grupo é novo e número inventado é pior que nenhum.
 */

/**
 * Script do <head> que posiciona o sol/lua ANTES da primeira pintura (ver
 * src/lib/ceu.ts). Só existe no HTML estático: o <Head> do vite-react-ssg quebra
 * no cliente (ver SeoHead), e no cliente o useEffect abaixo cobre o resto.
 */
function ScriptDoCeu() {
  if (!import.meta.env.SSR) return null;
  return (
    <Head>
      <script>{`(${aplicarCeu.toString()})(document.documentElement,new Date())`}</script>
    </Head>
  );
}

const CINCO_MINUTOS = 5 * 60 * 1000;

const ITENS = [
  { icone: "⛺", texto: "Barracas, sacos de dormir e isolantes" },
  { icone: "🔦", texto: "Lanternas, fogareiros e cozinha de acampamento" },
  { icone: "🎒", texto: "Mochilas, roupas e calçados de trilha" },
] as const;

const Camping = () => {
  const [abrindo, setAbrindo] = useState(false);

  // Reforço do script do <head>: cobre o HTML que veio do cache do service
  // worker (sem o script) e quem deixa a aba aberta virando o período.
  useEffect(() => {
    const raiz = document.documentElement;
    aplicarCeu(raiz, new Date());
    const id = window.setInterval(() => aplicarCeu(raiz, new Date()), CINCO_MINUTOS);
    return () => window.clearInterval(id);
  }, []);

  const entrar = async () => {
    if (abrindo) return;
    const url = buildOutboundUrl(GRUPO_CAMPING.whatsappUrl, {
      utm_source: "primata",
      utm_medium: "landing",
      utm_campaign: "camping",
    });
    // Um id só, espelhado no GA4 (cta_click) e no Lead do Pixel/CAPI (dedup)
    const eventId = generateEventId();
    setAbrindo(true);
    trackCtaClick("whatsapp", "camping", undefined, eventId);
    await trackLeadAndGo(url, "whatsapp", {
      location: "Camping",
      eventId,
      group: GRUPO_CAMPING,
    });
    setAbrindo(false);
  };

  return (
    <div className="camping">
      <SeoHead
        path="/camping"
        title="Macaco das Promos Camping 🏕️ — Promos de camping no seu WhatsApp"
        description="Barraca, saco de dormir, lanterna e tudo pra trilha com desconto de verdade. Entre grátis no grupo de camping do Macaco das Promos no WhatsApp."
        image="https://macacodaspromo.com.br/og-camping.jpg"
        imageAlt="Macaco das Promos de chapéu de explorador, acampando"
        themeColor="#EEF3E6"
        htmlClass="page-camping"
      />
      <ScriptDoCeu />

      {/* Céu e morros em camada fixa — atrás de tudo, não rola com a página.
          Sol, lua e estrelas seguem a hora do visitante (html[data-periodo]). */}
      <div className="camping-cenario" aria-hidden="true">
        <span className="camping-estrelas" />
        <span className="camping-astro camping-sol" />
        <span className="camping-astro camping-lua" />
        <svg
          className="camping-morros"
          viewBox="0 0 1440 360"
          preserveAspectRatio="none"
          focusable="false"
        >
          <path
            className="camping-morro-1"
            d="M0 210 L180 120 L330 190 L520 70 L700 180 L880 100 L1060 190 L1250 90 L1440 170 L1440 360 L0 360 Z"
          />
          <path
            className="camping-morro-2"
            d="M0 260 C160 200 300 230 460 250 C640 272 760 210 940 220 C1120 230 1260 270 1440 240 L1440 360 L0 360 Z"
          />
          <path
            className="camping-morro-3"
            d="M0 310 C200 280 380 300 560 306 C760 312 900 286 1100 292 C1260 297 1360 310 1440 304 L1440 360 L0 360 Z"
          />
        </svg>
      </div>

      <main className="camping-main">
        {/* Mascote e título SEM animação de entrada: são candidatos a LCP e
            nascer com opacity 0 atrasa a métrica numa landing de anúncio pago. */}
        <div className="camping-mascote">
          <img
            src={mascote}
            alt="Macaco das Promos de chapéu de explorador, com mochila e caneca, acampando"
            width={512}
            height={512}
            fetchPriority="high"
            decoding="async"
          />
          <p className="camping-selo">🏕️ Grupo novo · Camping</p>
        </div>

        <p className="camping-marca">Macaco das Promos</p>
        <h1 className="camping-titulo">
          Camping com <span>preço de bug</span>.
        </h1>
        {/* CTA ANTES da lista (2026-10-07): no teste a 1280x800 e num celular de
            667px o botão caía abaixo da dobra. Numa landing de um botão só, o
            botão visível sem rolar vale mais que a ordem "explica, depois pede". */}
        <div className="camping-entra" style={{ animationDelay: "120ms" }}>
          <button
            type="button"
            id="cta-whatsapp-camping"
            className="camping-cta"
            onClick={entrar}
            disabled={abrindo}
            aria-label="Entrar no grupo de Camping do WhatsApp"
          >
            <span className="camping-cta-icone" aria-hidden="true">
              <FaWhatsapp size={26} />
            </span>
            {abrindo ? (
              <span className="camping-cta-texto">Abrindo WhatsApp...</span>
            ) : (
              <span className="camping-cta-texto">
                <strong>Entrar no grupo de Camping</strong>
                <small>Grátis · direto no WhatsApp</small>
              </span>
            )}
          </button>

          <p className="camping-garantia">✓ 100% grátis · ✓ Sem spam · ✓ Saia quando quiser</p>
        </div>

        <section className="camping-lista camping-entra" style={{ animationDelay: "180ms" }}>
          <h2 className="camping-lista-titulo">O que cai no grupo</h2>
          <ul className="camping-itens">
            {ITENS.map((item) => (
              <li key={item.texto}>
                <span className="camping-item-icone" aria-hidden="true">
                  {item.icone}
                </span>
                {item.texto}
              </li>
            ))}
          </ul>
        </section>
      </main>

      <footer className="camping-rodape">
        <p>© 2026 Macaco das Promos</p>
        <p>Somos afiliados das lojas — você não paga nada a mais. 🐒</p>
      </footer>
    </div>
  );
};

export default Camping;
